import React, { useCallback } from 'react';
import { CardItem } from 'components/professor_area/CardItem';
import { Divider, MenuProps, Progress, Tag, Tooltip, Typography } from 'antd';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { IStudent, IStudentShow } from 'interfaces/professor_area/students';
import { formatDate } from 'services/date';
import { formatScoreOutOfTen } from 'utils/score-formatter';
import { getAPI } from 'requests/api';
import { getDisciplineStatusDisplay } from 'services/graduate-enrollment/discipline-status';
import { getInitialsName } from 'utils/get-initials-name';
import { useParams } from 'react-router-dom';

const { Text } = Typography;

const items: MenuProps['items'] = [
  {
    key: 'edit',
    label: 'Editar frequência',
    icon: <EditOutlined />
  },
  {
    key: 'show',
    label: 'Ver dados do aluno',
    icon: <EyeOutlined />
  }
];

export const StudentListItem: React.FC<{
  student: IStudent;
  handleEditFrequency: (student: IStudentShow) => void;
  handleShowDetails: (student: IStudentShow) => void;
  canEditFrequency: boolean;
  canShowDetails: boolean;
}> = ({
  student,
  handleEditFrequency,
  handleShowDetails,
  canEditFrequency,
  canShowDetails
}) => {
  const { id } = useParams();

  const getStudentData = useCallback(
    async (callback: typeof handleEditFrequency | typeof handleShowDetails) => {
      const studentData = await getAPI()
        .get(`/admin/professor_area/classrooms/${id}/enrollments/${student.id}`)
        .catch(() => {
          throw new Error('Não foi possivel encontrar os dados do aluno');
        });
      callback(studentData?.data);
    },
    [id, student.id]
  );

  const handleMenuClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      if (event.key === 'edit') {
        getStudentData(handleEditFrequency);
      } else {
        getStudentData(handleShowDetails);
      }
    },
    [getStudentData, handleEditFrequency, handleShowDetails]
  );

  const menuProps: MenuProps = {
    items: [
      ...(canEditFrequency ? [items[0]] : []),
      ...(canShowDetails ? [items[1]] : [])
    ],
    onClick: handleMenuClick
  };

  const academicStatus = getDisciplineStatusDisplay(student);
  const attendanceRate = student.attendance_rate;

  return (
    <CardItem
      avatar={getInitialsName(student.student_name)}
      title={student.student_name}
      menuProps={menuProps}
      meta={
        <div className="flex flex-col">
          <Text type="secondary" className="!leading-5">
            Matrícula: {student.enrollment_registration}
          </Text>
          <Text type="secondary" className="!leading-5">
            Curso: {student.course_name}
          </Text>
        </div>
      }
      extraContent={
        <div className="flex items-center text-left mr-4">
          <div className="flex items-center gap-2 w-[160px] flex-shrink-0">
            <Progress
              type="circle"
              percent={attendanceRate ?? 0}
              size={24}
              showInfo={false}
            />
            <Text type="secondary" className="whitespace-nowrap">
              {attendanceRate ?? '0'}% de frequência
            </Text>
          </div>
          <Divider type="vertical" />
          <div className="flex items-center gap-1 w-[170px] flex-shrink-0">
            {student.score !== null && student.score !== undefined ? (
              <Text type="secondary" className="whitespace-nowrap">
                Nota: {formatScoreOutOfTen(student.score)}
              </Text>
            ) : (
              <>
                <Text type="secondary" className="whitespace-nowrap">
                  Nota: {formatScoreOutOfTen(student.partial_score)}
                </Text>
                {student.partial_score !== null &&
                  student.partial_score !== undefined && (
                    <Tooltip
                      title={
                        student.partial_score_updated_at
                          ? `Atualizado em ${formatDate(
                              student.partial_score_updated_at,
                              'DD/MM/YYYY HH:mm'
                            )}`
                          : undefined
                      }
                    >
                      <Tag color="blue" className="!m-0">
                        parcial
                      </Tag>
                    </Tooltip>
                  )}
              </>
            )}
          </div>
          <Divider type="vertical" />
          <Tag color={academicStatus.color} className="!m-0">
            {academicStatus.label}
          </Tag>
        </div>
      }
    />
  );
};
